/**
 * What kind of place a tenant is. A pharmacy that bought the pharmacy plan
 * is a pharmacy — not a "hospital" with most of its menu missing — and the
 * sign-in page, greeting and dashboard should say so.
 */
export const FACILITY_LABELS = {
    hospital: 'Hospital',
    lab: 'Laboratory',
    pharmacy: 'Pharmacy',
};
/** Modules that make a tenant a hospital whatever else it has. */
const HOSPITAL_MODULES = new Set(['opd', 'ipd', 'er', 'ot', 'blood_bank', 'maternity', 'dialysis', 'dental']);
/**
 * The facility type a bundle of modules implies, for when the operator did
 * not say. Any clinical module means hospital; otherwise a lone lab is a
 * laboratory and a lone pharmacy is a pharmacy. Lab + pharmacy with nothing
 * clinical is still best served by the hospital shell.
 */
export function inferFacilityType(moduleFlags) {
    const modules = new Set(moduleFlags);
    if ([...modules].some((code) => HOSPITAL_MODULES.has(code)))
        return 'hospital';
    const hasLab = modules.has('lab') || modules.has('radiology');
    const hasPharmacy = modules.has('pharmacy') || modules.has('inventory');
    if (hasPharmacy && !hasLab)
        return 'pharmacy';
    if (hasLab && !hasPharmacy)
        return 'lab';
    return 'hospital';
}
export const EVERY_PACKAGE_MODULES = ['core', 'billing', 'accounting', 'hr', 'reports', 'notifications', 'feedback', 'tasks', 'portal'];
const INSURANCE_MODULES = ['insurance'];
export const OPD_MODULES = [...EVERY_PACKAGE_MODULES, ...INSURANCE_MODULES, 'opd', 'dental', 'mrd'];
export const IPD_MODULES = [
    ...EVERY_PACKAGE_MODULES,
    ...INSURANCE_MODULES,
    'ipd',
    'er',
    'ot',
    'maternity',
    'dialysis',
    'daycare',
    'blood_bank',
    'ambulance',
    'dietary',
    'housekeeping',
    'mrd',
];
export const LABORATORY_MODULES = [
    'core', 'lab', 'radiology', 'blood_bank', 'inventory', 'billing', 'insurance', 'nphies',
    'accounting', 'hr', 'reports', 'notifications', 'feedback', 'tasks', 'portal',
];
export const MEDICAL_STORE_MODULES = [
    'core', 'pharmacy', 'inventory', 'billing', 'insurance', 'accounting', 'hr',
    'reports', 'notifications', 'feedback', 'tasks', 'portal', 'wasfaty', 'nphies',
];
/**
 * Which modules a plan for this kind of place may include.
 *
 * A medical store has no wards to run and no theatre to schedule, so offering
 * those on its plan only invites someone to sell it something it cannot use.
 * A hospital is the general case and takes everything.
 */
export function modulesForFacilityType(type) {
    if (type === 'lab')
        return LABORATORY_MODULES;
    if (type === 'pharmacy')
        return MEDICAL_STORE_MODULES;
    return null;
}
export function moduleSuitsFacilityType(code, type) {
    const allowed = modulesForFacilityType(type);
    return allowed === null || allowed.includes(code);
}
/** Limits that mean nothing for a place of this kind. */
export function limitSuitsFacilityType(limit, type) {
    if (limit === 'beds')
        return type === 'hospital';
    return true;
}
//# sourceMappingURL=facility.js.map